import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  ChevronDownIcon,
  UserGroupIcon,
  TrophyIcon,
  GlobeAltIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  UsersIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import PlayerDetailsModal from '../components/PlayerDetailsModal';
import { getPlayersByTournament, updatePlayer, deletePlayer, updatePlayerStatus, updateRegistrationStatus, isValidUUID, generateRegistrationNumbers } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import CountUp from 'react-countup';
import ReservedSpotsManager from '../components/ReservedSpotsManager';
import RegistrationModal from '../components/RegistrationModal';
import { sendConfirmationEmail } from '../lib/email';
import PlayerPairingsTab from '../components/PlayerPairingsTab';
import PlayerAnalyticsTab from './players/PlayerAnalyticsTab';

type ActiveTab = 'players' | 'pairings' | 'analytics';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  resident_club: string;
  handicap: number;
  category: string;
  status: string;
  created_at: string;
  country: string;
  tournament_id: string;
  registration_status: {
    personalInfo: boolean;
    payment: boolean;
    verification: boolean;
  };
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
}

interface FormattedPlayer {
  id: number;
  name: string;
  email: string;
  phone: string;
  club: string;
  handicap: number;
  category: string;
  status: string;
  registrationDate: string;
  paymentStatus: string;
  country: string;
  registrationStatus: {
    personalInfo: boolean;
    payment: boolean;
    verification: boolean;
  };
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
}

const statuses = ["All", "Active", "Pending", "Withdrawn"];
const paymentStatuses = ["All", "Paid", "Pending", "Refunded"];

const playerStats = {
  totalPlayers: {
    count: 0,
    trend: "+0%",
    isPositive: true
  },
  averageHandicap: {
    value: 0,
    trend: "0",
    isPositive: true
  },
  internationalPlayers: {
    count: 0,
    percentage: "0%",
    countries: 0
  },
  registrationProgress: {
    registered: 0,
    capacity: 300,
    percentage: "0%"
  },
  pendingPlayers: 0,
};

interface PlayersProps {
  tournamentId: string;
}

function Players({ tournamentId }: PlayersProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('players');
  const [players, setPlayers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedPayment, setSelectedPayment] = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<FormattedPlayer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(playerStats);
  const [isExporting, setIsExporting] = useState(false);
  const [showReservedSpotsManager, setShowReservedSpotsManager] = useState(false);
  const [isGeneratingRegNumbers, setIsGeneratingRegNumbers] = useState(false);
  const [tournamentCapacity, setTournamentCapacity] = useState(300);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [categories, setCategories] = useState<string[]>(['All']);
  const [sortKey, setSortKey] = useState<'name' | 'country' | 'club' | 'handicap' | 'status' | 'paymentStatus' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const recordsPerPageOptions = [5, 10, 25, 50];

  useEffect(() => {
    if (tournamentId && isValidUUID(tournamentId)) {
      fetchPlayers();
      fetchCategories();
    }
  }, [tournamentId]);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('tournament_categories')
      .select('name')
      .eq('tournament_id', tournamentId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (data && data.length > 0) {
      setCategories(['All', ...data.map((c: { name: string }) => c.name)]);
    }
  };

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const { data, error } = await getPlayersByTournament(tournamentId);
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setPlayers(data);
        
        // Get tournament capacity
        const { data: tournamentData, error: tournamentError } = await supabase
          .from('tournaments')
          .select('capacity')
          .eq('id', tournamentId)
          .single();
          
        const fetchedCapacity = (!tournamentError && tournamentData) ? tournamentData.capacity : tournamentCapacity;
        if (!tournamentError && tournamentData) {
          setTournamentCapacity(fetchedCapacity);
        }

        // Calculate stats
        const totalCount = data.length;
        const registeredCount = data.filter(p => p.registration_status?.payment === true).length;
        const pendingCount = totalCount - registeredCount;
        const internationalCount = data.filter(p => p.country && p.country !== 'Nigeria').length;
        const uniqueCountries = new Set(data.filter(p => p.country).map(p => p.country)).size;
        const totalHandicap = data.reduce((sum, p) => sum + (p.handicap || 0), 0);
        const avgHandicap = totalCount > 0 ? totalHandicap / totalCount : 0;

        setStats({
          totalPlayers: {
            count: totalCount,
            trend: '',
            isPositive: true
          },
          averageHandicap: {
            value: parseFloat(avgHandicap.toFixed(1)),
            trend: '',
            isPositive: true
          },
          internationalPlayers: {
            count: internationalCount,
            percentage: `${Math.round((internationalCount / (totalCount || 1)) * 100)}%`,
            countries: uniqueCountries
          },
          registrationProgress: {
            registered: registeredCount,
            capacity: fetchedCapacity,
            percentage: `${Math.round((totalCount / fetchedCapacity) * 100)}%`
          },
          pendingPlayers: pendingCount,
        });
      }
    } catch (err) {
      console.error('Error fetching players:', err);
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const formattedPlayers = useMemo(() => {
    return players.map(player => {
      // Ensure registration_status is properly handled
      const registrationStatus = player.registration_status || { personalInfo: false, payment: false, verification: false };
      
      // Convert string ID to number for display purposes
      const numericId = player.id ? parseInt(player.id.replace(/-/g, '').substring(0, 8), 16) : 0;
      
      return {
        id: numericId,
        name: `${player.first_name || ''} ${player.last_name || ''}`.trim(),
        email: player.email || '',
        phone: player.phone || '',
        club: player.resident_club || '',
        handicap: player.handicap || 0,
        category: player.category || 'Amateur',
        status: player.status ? player.status.charAt(0).toUpperCase() + player.status.slice(1).toLowerCase() : 'Pending',
        registrationDate: player.created_at ? new Date(player.created_at).toISOString().split('T')[0] : '',
        paymentStatus: registrationStatus.payment ? 'Paid' : 'Pending',
        country: player.country || 'Nigeria',
        registrationStatus,
        payment_proof_url: player.payment_proof_url,
        shirt_size: player.shirt_size,
        registration_number: player.registration_number,
        gender: player.gender
      };
    });
  }, [players]);

  const filteredPlayers = useMemo(() => {
    const filtered = formattedPlayers.filter(player => {
      const searchMatch = searchTerm.toLowerCase() === '' ||
        player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (player.phone && player.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (player.club && player.club.toLowerCase().includes(searchTerm.toLowerCase()));

      const categoryMatch = selectedCategory === 'All' || player.category === selectedCategory;
      const statusMatch = selectedStatus === 'All' || player.status === selectedStatus;
      const paymentMatch = selectedPayment === 'All' || player.paymentStatus === selectedPayment;

      return searchMatch && categoryMatch && statusMatch && paymentMatch;
    });

    if (!sortKey) return filtered;

    return [...filtered].sort((a, b) => {
      const aVal = sortKey === 'handicap' ? (parseFloat(String(a[sortKey])) || 0) : String(a[sortKey] ?? '').toLowerCase();
      const bVal = sortKey === 'handicap' ? (parseFloat(String(b[sortKey])) || 0) : String(b[sortKey] ?? '').toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [formattedPlayers, searchTerm, selectedCategory, selectedStatus, selectedPayment, sortKey, sortDir]);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredPlayers.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredPlayers.slice(indexOfFirstRecord, indexOfLastRecord);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleRecordsPerPageChange = (value: number) => {
    setRecordsPerPage(value);
    setCurrentPage(1);
  };

  const handlePlayerClick = (player: FormattedPlayer) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  const handleAddPlayer = () => {
    setIsRegistrationModalOpen(true);
  };

  const handleApprovePayment = async (paymentId: number) => {
    if (!selectedPlayer) return;
    
    try {
      setIsProcessingPayment(true);
      
      // Find the original player by matching the numeric ID
      const originalPlayer = players.find(p => {
        const numericId = p.id ? parseInt(p.id.replace(/-/g, '').substring(0, 8), 16) : 0;
        return numericId === selectedPlayer.id;
      });
      
      if (!originalPlayer) {
        toast.error('Player not found');
        return;
      }
      
      const updatedRegistrationStatus = {
        ...originalPlayer.registration_status,
        payment: true
      };
      
      const { error } = await updateRegistrationStatus(
        originalPlayer.id,
        updatedRegistrationStatus
      );
      
      if (error) throw error;
      
      // Create a financial transaction for this payment
      const { data: tournament, error: tournamentError } = await supabase
        .from('tournaments')
        .select('registration_fee')
        .eq('id', originalPlayer.tournament_id)
        .single();
      
      if (tournamentError) throw tournamentError;
      
      // Check if a transaction already exists
      const { data: existingTransaction } = await supabase
        .from('financial_transactions')
        .select('id')
        .eq('tournament_id', originalPlayer.tournament_id)
        .eq('player_id', originalPlayer.id)
        .eq('type', 'income')
        .eq('category', 'registration')
        .maybeSingle();
      
      if (!existingTransaction) {
        // Create a new transaction only if one doesn't exist
        const { error: createError } = await supabase
          .from('financial_transactions')
          .insert([{
            tournament_id: originalPlayer.tournament_id,
            player_id: originalPlayer.id,
            type: 'income',
            category: 'registration',
            description: `Registration fee - ${originalPlayer.first_name} ${originalPlayer.last_name}`,
            amount: tournament.registration_fee,
            transaction_date: new Date().toISOString().split('T')[0],
            status: 'completed',
            payment_method: 'Bank Transfer',
            reference_number: originalPlayer.registration_number || `REG-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
          }]);
        
        if (createError) {
          console.error('Error creating registration transaction:', createError);
        }
      }
      
      // Update local state
      const updatedPlayers = players.map(p => {
        if (p.id === originalPlayer.id) {
          return {
            ...p,
            registration_status: updatedRegistrationStatus
          };
        }
        return p;
      });
      
      setPlayers(updatedPlayers);
      
      // Update selected player
      if (selectedPlayer) {
        setSelectedPlayer({
          ...selectedPlayer,
          paymentStatus: 'Paid',
          registrationStatus: updatedRegistrationStatus
        });
      }
      
      toast.success('Payment approved successfully');
    } catch (err) {
      console.error('Error approving payment:', err);
      toast.error('Failed to approve payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleRejectPayment = async (paymentId: number) => {
    if (!selectedPlayer) return;
    
    try {
      // Find the original player by matching the numeric ID
      const originalPlayer = players.find(p => {
        const numericId = p.id ? parseInt(p.id.replace(/-/g, '').substring(0, 8), 16) : 0;
        return numericId === selectedPlayer.id;
      });
      
      if (!originalPlayer) {
        toast.error('Player not found');
        return;
      }
      
      const updatedRegistrationStatus = {
        ...originalPlayer.registration_status,
        payment: false
      };
      
      const { error } = await updateRegistrationStatus(
        originalPlayer.id,
        updatedRegistrationStatus
      );
      
      if (error) throw error;
      
      // Update local state
      const updatedPlayers = players.map(p => {
        if (p.id === originalPlayer.id) {
          return {
            ...p,
            registration_status: updatedRegistrationStatus
          };
        }
        return p;
      });
      
      setPlayers(updatedPlayers);
      
      // Update selected player
      if (selectedPlayer) {
        setSelectedPlayer({
          ...selectedPlayer,
          paymentStatus: 'Rejected',
          registrationStatus: updatedRegistrationStatus
        });
      }
      
      toast.success('Payment rejected');
    } catch (err) {
      console.error('Error rejecting payment:', err);
      toast.error('Failed to reject payment');
    }
  };

  const handleEditPlayer = async (player: FormattedPlayer) => {
    try {
      // Find the original player by matching the numeric ID
      const originalPlayer = players.find(p => {
        const numericId = p.id ? parseInt(p.id.replace(/-/g, '').substring(0, 8), 16) : 0;
        return numericId === player.id;
      });
      
      if (!originalPlayer) {
        toast.error('Player not found');
        return;
      }
      
      const nameParts = player.name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');
      
      const { error } = await updatePlayer(originalPlayer.id, {
        first_name: firstName,
        last_name: lastName,
        email: player.email,
        phone: player.phone,
        country: player.country,
        resident_club: player.club,
        handicap: player.handicap,
        category: player.category,
        status: player.status,
        registration_status: player.registrationStatus,
        shirt_size: player.shirt_size,
        registration_number: player.registration_number,
        gender: player.gender
      });

      if (error) throw error;
      
      await fetchPlayers();
      toast.success('Player updated successfully');
    } catch (err) {
      console.error('Error updating player:', err);
      toast.error('Failed to update player');
    }
  };

  const handleDeletePlayer = async (playerId: number) => {
    try {
      // Find the original player by matching the numeric ID
      const originalPlayer = players.find(p => {
        const numericId = p.id ? parseInt(p.id.replace(/-/g, '').substring(0, 8), 16) : 0;
        return numericId === playerId;
      });
      
      if (!originalPlayer) {
        toast.error('Player not found');
        return;
      }
      
      const { error } = await deletePlayer(originalPlayer.id);
      if (error) throw error;
      
      await fetchPlayers();
      setIsModalOpen(false);
      toast.success('Player deleted successfully');
    } catch (err) {
      console.error('Error deleting player:', err);
      toast.error('Failed to delete player');
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const exportPlayers = async () => {
    try {
      setIsExporting(true);

      const playerIds = players.map(p => p.id);
      const { data: transactions } = await supabase
        .from('financial_transactions')
        .select('player_id, amount')
        .in('player_id', playerIds)
        .eq('type', 'income')
        .eq('status', 'completed');

      const amountByPlayer: Record<string, number> = {};
      if (transactions) {
        for (const t of transactions) {
          if (t.player_id) {
            amountByPlayer[t.player_id] = (amountByPlayer[t.player_id] || 0) + Number(t.amount);
          }
        }
      }

      const playerIdByNumericId: Record<number, string> = {};
      for (const p of players) {
        const numericId = p.id ? parseInt(p.id.replace(/-/g, '').substring(0, 8), 16) : 0;
        playerIdByNumericId[numericId] = p.id;
      }

      const headers = ['Name', 'Email', 'Phone', 'Country', 'Club', 'Handicap', 'Category', 'Status', 'Registration Date', 'Payment Status', 'Amount Paid', 'Registration Number', 'Shirt Size'];
      const csvContent = [
        headers.join(','),
        ...filteredPlayers.map(player => {
          const rawId = playerIdByNumericId[player.id] || '';
          const amountPaid = amountByPlayer[rawId] ?? '';
          return [
            `"${player.name}"`,
            `"${player.email}"`,
            `"${player.phone}"`,
            `"${player.country}"`,
            `"${player.club}"`,
            player.handicap,
            `"${player.category}"`,
            `"${player.status}"`,
            `"${player.registrationDate}"`,
            `"${player.paymentStatus}"`,
            amountPaid,
            `"${player.registration_number || ''}"`,
            `"${player.shirt_size || ''}"`,
          ].join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'players.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Players data exported successfully');
    } catch (err) {
      console.error('Error exporting players:', err);
      toast.error('Failed to export players data');
    } finally {
      setIsExporting(false);
    }
  };

  // Function to handle player registration completion from the admin side
  const handleRegistrationComplete = async (playerData: any) => {
    try {
      // Fetch the newly created player to get their ID
      const { data: newPlayers, error } = await getPlayersByTournament(tournamentId);
      
      if (error) throw error;
      
      if (newPlayers && newPlayers.length > 0) {
        // Find the newly added player (assuming it's the most recent one)
        const newPlayer = newPlayers.find(p => 
          p.email === playerData.email && 
          p.first_name === playerData.firstName && 
          p.last_name === playerData.lastName
        );
        
        if (newPlayer) {
          // Generate a registration number
          const regNumber = `IBO-${new Date().getFullYear()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
          
          // Update the player with the registration number
          const { error: updateError } = await updatePlayer(newPlayer.id, {
            registration_number: regNumber
          });
          
          if (updateError) throw updateError;
          
          // Send confirmation email
          await sendConfirmationEmail(
            newPlayer.email,
            newPlayer.first_name,
            regNumber,
            newPlayer.shirt_size,
            newPlayer.last_name
          );
          
          toast.success('Player added and confirmation email sent');
        }
      }
      
      // Refresh the player list
      fetchPlayers();
    } catch (err) {
      console.error('Error handling registration completion:', err);
      toast.error('Player added but failed to send confirmation email');
      // Still refresh the player list even if email sending fails
      fetchPlayers();
    }
  };

  const handleGenerateRegistrationNumbers = async () => {
    try {
      setIsGeneratingRegNumbers(true);
      const result = await generateRegistrationNumbers();
      
      if (result.success) {
        toast.success(result.message);
        // Refresh the player list to show the new registration numbers
        await fetchPlayers();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error generating registration numbers:', error);
      toast.error('Failed to generate registration numbers');
    } finally {
      setIsGeneratingRegNumbers(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-golf-gray">Players Management</h2>
        {activeTab === 'players' && (
          <div className="flex gap-2">
            <button
              onClick={handleGenerateRegistrationNumbers}
              disabled={isGeneratingRegNumbers}
              className="px-4 py-2 bg-golf-green text-white font-medium rounded-lg hover:bg-golf-green/90 transition-colors inline-flex items-center space-x-2"
            >
              <span>{isGeneratingRegNumbers ? 'Generating...' : 'Generate Reg Numbers'}</span>
            </button>
            <button
              onClick={exportPlayers}
              disabled={isExporting}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>Export Players</span>
            </button>
            <button
              onClick={handleAddPlayer}
              className="modern-button px-4 py-2 text-white font-medium inline-flex items-center space-x-2"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Add Player</span>
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
        <button
          onClick={() => setActiveTab('players')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'players'
              ? 'bg-white text-golf-gray shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <UserGroupIcon className="w-4 h-4" />
          Players
        </button>
        <button
          onClick={() => setActiveTab('pairings')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'pairings'
              ? 'bg-white text-golf-gray shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <UsersIcon className="w-4 h-4" />
          Pairings
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'analytics'
              ? 'bg-white text-golf-gray shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ChartBarIcon className="w-4 h-4" />
          Analytics
        </button>
      </div>

      {activeTab === 'pairings' && (
        <PlayerPairingsTab tournamentId={tournamentId} />
      )}

      {activeTab === 'analytics' && (
        <PlayerAnalyticsTab players={players} />
      )}

      {activeTab === 'players' && (
        <>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-blue-500/10 p-3 rounded-xl">
              <UserGroupIcon className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Players</p>
              <div className="flex items-center gap-2">
                <CountUp
                  end={stats.totalPlayers.count}
                  duration={2}
                  className="text-2xl font-bold text-golf-gray"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.pendingPlayers}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Capacity</div>
              <div className="text-lg font-semibold text-golf-gray">{stats.registrationProgress.capacity}</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-green-500/10 p-3 rounded-xl">
              <TrophyIcon className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Average Handicap</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-golf-gray">{stats.averageHandicap.value}</span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-teal-500/10 p-3 rounded-xl">
              <GlobeAltIcon className="w-6 h-6 text-teal-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">International Players</p>
              <div className="flex items-center gap-2">
                <CountUp
                  end={stats.internationalPlayers.count}
                  duration={2}
                  className="text-2xl font-bold text-golf-gray"
                />
                <span className="text-sm text-gray-500">
                  ({stats.internationalPlayers.countries} countries)
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-orange-500/10 p-3 rounded-xl">
              <ChartBarIcon className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Registration Progress</p>
              <div className="flex items-center gap-2">
                <CountUp
                  end={parseInt(stats.registrationProgress.percentage)}
                  duration={2}
                  suffix="%"
                  className="text-2xl font-bold text-golf-gray"
                />
                <span className="text-sm text-gray-500">
                  ({stats.registrationProgress.registered}/{stats.registrationProgress.capacity})
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-golf-orange rounded-full"
              style={{ width: stats.registrationProgress.percentage }}
            />
          </div>
        </motion.div>
      </div>

      {/* Reserved Spots Manager */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
      >
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-golf-gray">Registration Capacity Management</h2>
            <p className="text-sm text-gray-500 mt-1">Manage reserved spots and player registration capacity</p>
          </div>
          <button
            onClick={() => setShowReservedSpotsManager(!showReservedSpotsManager)}
            className="px-4 py-2 bg-golf-green text-white font-medium rounded-lg hover:bg-golf-green/90 transition-colors flex items-center gap-2"
          >
            {showReservedSpotsManager ? 'Hide Manager' : 'Show Manager'}
          </button>
        </div>

        {showReservedSpotsManager && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ReservedSpotsManager />
          </motion.div>
        )}
      </motion.div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              />
            </div>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">Filters</span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                value={selectedPayment}
                onChange={(e) => { setSelectedPayment(e.target.value); setCurrentPage(1); }}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                {paymentStatuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </motion.div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 md:px-6 py-3 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Show</span>
            <select
              value={recordsPerPage}
              onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded-lg text-sm focus:ring-golf-orange focus:border-golf-orange"
            >
              {recordsPerPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">entries</span>
          </div>
          <div className="text-sm text-gray-500 text-center sm:text-right">
            {loading ? (
              'Loading players...'
            ) : (
              `Showing ${filteredPlayers.length > 0 ? indexOfFirstRecord + 1 : 0} to ${Math.min(indexOfLastRecord, filteredPlayers.length)} of ${filteredPlayers.length} entries`
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {([
                      { label: 'Player', key: 'name' },
                      { label: 'Contact', key: null },
                      { label: 'Details', key: 'club' },
                      { label: 'Registration Progress', key: null },
                      { label: 'Status', key: 'status' },
                    ] as { label: string; key: typeof sortKey }[]).map(({ label, key }) => (
                      <th
                        key={label}
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${key ? 'cursor-pointer select-none hover:bg-gray-100' : ''}`}
                        onClick={() => key && handleSort(key)}
                      >
                        <div className="flex items-center gap-1">
                          {label}
                          {key && (
                            sortKey === key
                              ? <ChevronUpIcon className={`h-3.5 w-3.5 text-golf-orange transition-transform ${sortDir === 'desc' ? 'rotate-180' : ''}`} />
                              : <ChevronUpDownIcon className="h-3.5 w-3.5 text-gray-400" />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentRecords.length > 0 ? (
                    currentRecords.map((player) => (
                      <tr
                        key={player.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handlePlayerClick(player)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-golf-orange/10 flex items-center justify-center">
                                <span className="text-golf-orange font-medium">
                                  {player.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{player.name}</div>
                              <div className="text-sm text-gray-500">{player.country}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{player.email}</div>
                          <div className="text-sm text-gray-500">{player.phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{player.club}</div>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <span>HC: {player.handicap}</span>
                            <span>•</span>
                            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                              {player.category}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">Progress</span>
                              <span className="font-medium text-gray-900">
                                {Object.values(player.registrationStatus).filter(Boolean).length}/3
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-golf-orange rounded-full h-2 transition-all duration-300"
                                style={{
                                  width: `${(Object.values(player.registrationStatus).filter(Boolean).length / 3) * 100}%`
                                }}
                              />
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <div className={`flex items-center ${player.registrationStatus.personalInfo ? 'text-green-500' : 'text-gray-400'}`}>
                                <span className="w-1.5 h-1.5 rounded-full mr-1 bg-current" />
                                Info
                              </div>
                              <div className={`flex items-center ${player.registrationStatus.payment ? 'text-green-500' : 'text-gray-400'}`}>
                                <span className="w-1.5 h-1.5 rounded-full mr-1 bg-current" />
                                Payment
                              </div>
                              <div className={`flex items-center ${player.registrationStatus.verification ? 'text-green-500' : 'text-gray-400'}`}>
                                <span className="w-1.5 h-1.5 rounded-full mr-1 bg-current" />
                                Verified
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              player.status?.toLowerCase() === 'active'
                                ? 'bg-green-100 text-green-800'
                                : player.status?.toLowerCase() === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {player.status}
                            </span>
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              player.paymentStatus === 'Paid'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {player.paymentStatus}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                        No players found matching the current filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden">
              {currentRecords.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {currentRecords.map((player) => (
                    <div
                      key={player.id}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handlePlayerClick(player)}
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="h-12 w-12 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-golf-orange font-medium">
                            {player.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-golf-gray">{player.name}</h3>
                          <p className="text-sm text-gray-500">{player.email}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        <div>
                          <span className="block text-gray-500">Club</span>
                          <span className="font-medium text-golf-gray">{player.club}</span>
                        </div>
                        <div>
                          <span className="block text-gray-500">Handicap</span>
                          <span className="font-medium text-golf-gray">{player.handicap}</span>
                        </div>
                        <div>
                          <span className="block text-gray-500">Category</span>
                          <span className="font-medium text-golf-gray">{player.category}</span>
                        </div>
                        <div>
                          <span className="block text-gray-500">Status</span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            player.status?.toLowerCase() === 'active'
                              ? 'bg-green-100 text-green-800'
                              : player.status?.toLowerCase() === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {player.status}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Registration Progress</span>
                          <span className="font-medium text-golf-gray">
                            {Object.values(player.registrationStatus).filter(Boolean).length}/3
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-golf-orange rounded-full h-2 transition-all duration-300"
                            style={{
                              width: `${(Object.values(player.registrationStatus).filter(Boolean).length / 3) * 100}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No players found matching the current filters
                </div>
              )}
            </div>
          </>
        )}

        <div className="px-4 md:px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-center sm:justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            
            <div className="hidden sm:flex items-center space-x-2">
              {getPageNumbers().map((number) => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  className={`px-3 py-1 rounded-lg ${
                    currentPage === number
                      ? 'bg-golf-orange text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {number}
                </button>
              ))}
            </div>

            <div className="sm:hidden">
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages || 1}
              </span>
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`p-2 rounded-lg ${
                currentPage === totalPages || totalPages === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <PlayerDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        player={selectedPlayer}
        tournamentId={tournamentId}
        onApprovePayment={handleApprovePayment}
        onRejectPayment={handleRejectPayment}
        onEditPlayer={handleEditPlayer}
        onDeletePlayer={handleDeletePlayer}
      />

      <RegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={() => {
          setIsRegistrationModalOpen(false);
          fetchPlayers();
        }}
        onSuccess={handleRegistrationComplete}
      />
      </>
      )}
    </div>
  );
}

export default Players;